import React from 'react'
import { useNavigate } from 'react-router-dom'
import { getThemeColors, THEME_OPTIONS } from '../theme'
import Header from './Header'
import Footer from './Footer'

const FAQS = [
  {
    category: 'Beta y acceso',
    items: [
      {
        q: '¿Qué significa que JobPipeline está en beta controlada?',
        a: 'Significa que el producto ya funciona, pero todavía estamos afinando seguridad, operación y experiencia antes de abrirlo a más usuarios. Algunas funciones pueden cambiar, ajustarse o presentar errores mientras validamos la beta.',
      },
      {
        q: '¿Por qué tengo que aceptar Términos y Aviso de Privacidad antes de entrar?',
        a: 'Porque necesitamos dejar claro cómo funciona la beta, qué puedes esperar del servicio y cómo tratamos tu información. La aceptación también nos ayuda a registrar la versión legal vigente con la que entraste a JobPipeline.',
      },
      {
        q: '¿Aceptar la beta significa que me garantizan entrevistas o empleo?',
        a: 'No. JobPipeline te ayuda a encontrar, filtrar y priorizar oportunidades con más claridad, pero no garantiza entrevistas, respuestas ni contrataciones.',
      },
      {
        q: '¿Me pueden quitar el acceso a la beta?',
        a: 'Sí. Durante la beta controlada podemos limitar, pausar o revocar accesos si detectamos riesgos de seguridad, abuso de la plataforma o ajustes operativos necesarios.',
      },
    ],
  },
  {
    category: 'Registro y entrada',
    items: [
      {
        q: '¿Cómo me registro?',
        a: 'Desde la pantalla de login puedes crear tu cuenta con correo y contraseña. Después de confirmar tu correo, podrás entrar y completar la aceptación legal de beta antes de usar el producto.',
      },
      {
        q: '¿Por qué no me deja entrar inmediatamente después de registrarme?',
        a: 'Puede pasar por tres razones comunes: todavía no confirmas tu correo, aún no aceptas los documentos legales de beta, o tu acceso beta todavía no está activo.',
      },
      {
        q: '¿Qué pasa si olvidé mi contraseña?',
        a: 'Puedes usar la opción de recuperación desde login. Recibirás un correo para crear una nueva contraseña y volver a entrar.',
      },
      {
        q: '¿Qué hago si ya tengo cuenta pero veo una pantalla de acceso beta?',
        a: 'Eso normalmente significa que tu cuenta existe, pero tu acceso beta todavía no está habilitado o sigue pendiente de revisión. No necesitas registrarte otra vez.',
      },
    ],
  },
  {
    category: 'Perfil y configuración inicial',
    items: [
      {
        q: '¿Necesito subir mi CV para usar JobPipeline?',
        a: 'No es obligatorio, pero ayuda. Si subes tu CV, JobPipeline puede usarlo como contexto para perfilar mejor tu búsqueda inicial y sugerir mejores keywords.',
      },
      {
        q: '¿Qué son las keywords y por qué importan?',
        a: 'Son los roles o búsquedas que quieres que JobPipeline siga por ti. Las keywords ayudan a enfocar el tipo de vacantes que se revisan y reducen el ruido desde el inicio.',
      },
      {
        q: '¿Puedo cambiar mi perfil o mis preferencias después?',
        a: 'Sí. Tu perfil, ubicación, modalidad y otros datos de búsqueda pueden ajustarse después para refinar resultados.',
      },
    ],
  },
  {
    category: 'Resultados y lectura general',
    items: [
      {
        q: '¿Cómo debo interpretar los resultados?',
        a: 'Tómalos como una ayuda para decidir más rápido. JobPipeline organiza oportunidades para que distingas cuáles vale la pena revisar primero, cuáles se ven más fuertes y cuáles podrían requerir más criterio antes de aplicar.',
      },
      {
        q: '¿Qué significa que una vacante se vea mejor posicionada que otra?',
        a: 'A alto nivel, significa que el sistema detectó una mejor combinación entre tu perfil, tu intención de búsqueda y el contexto general de la vacante. No es una verdad absoluta; sigue siendo una guía para priorizar.',
      },
      {
        q: '¿El score es una recomendación automática de aplicar?',
        a: 'No por sí solo. El score ayuda a ordenar y enfocar, pero la decisión final siempre debe considerar el rol, la empresa, la modalidad, el contexto y tu propio criterio profesional.',
      },
      {
        q: '¿Por qué a veces no veo muchas vacantes?',
        a: 'Menos resultados no siempre significa algo malo. En JobPipeline preferimos mostrar oportunidades con más sentido en lugar de llenar el dashboard con ruido o coincidencias débiles.',
      },
      {
        q: '¿Por qué no explican exactamente todo el proceso interno?',
        a: 'Porque queremos mantener una experiencia clara sin exponer detalles internos que pueden cambiar durante la beta. Lo importante para el usuario es entender que la plataforma busca reducir ruido y apoyar mejores decisiones.',
      },
    ],
  },
  {
    category: 'Feedback, soporte y operación',
    items: [
      {
        q: '¿Cómo doy feedback de producto?',
        a: 'Escríbenos a feedback@cuneo.pro. Úsalo para bugs, ideas, fricciones de UX o cualquier mejora que te gustaría ver dentro de JobPipeline.',
      },
      {
        q: '¿Cómo contacto al equipo por otros temas?',
        a: 'Puedes escribir a contacto@cuneo.pro para soporte general, dudas comerciales, alianzas o asuntos no relacionados directamente con feedback de producto.',
      },
      {
        q: '¿Qué conviene incluir cuando reporto un problema?',
        a: 'Qué intentabas hacer, qué esperabas que pasara, qué pasó realmente y, si aplica, screenshots o pasos para reproducir el problema.',
      },
      {
        q: '¿Qué hago si algo no carga o parece fallar?',
        a: 'Primero intenta recargar la página y volver a entrar. Si el problema persiste, repórtalo por correo con el mayor contexto posible para que podamos revisarlo rápido.',
      },
    ],
  },
]

export default function Faqs({ theme = THEME_OPTIONS.LIGHT, session = null }) {
  const C = getThemeColors(theme)
  const navigate = useNavigate()
  const search = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
  const fromBetaAccess = search?.get('from') === 'beta-access'
  const fromRegister = search?.get('from') === 'register'

  const sectionStyle = {
    background: C.white,
    border: `0.5px solid ${C.border}`,
    borderRadius: '18px',
    padding: '24px',
    boxShadow: C.shadow,
  }

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <Header theme={theme} session={session} perfil={null} />

      <div style={{ maxWidth: '1080px', margin: '0 auto', padding: 'clamp(16px, 3vw, 32px)', boxSizing: 'border-box' }}>
        <div style={{ ...sectionStyle, marginBottom: '18px' }}>
          <div style={{ fontSize: '11px', color: C.blue, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: '600', marginBottom: '8px' }}>FAQs</div>
          <div style={{ fontSize: '34px', fontWeight: '500', letterSpacing: '-1px', lineHeight: '1.1', marginBottom: '10px' }}>Preguntas frecuentes</div>
          <div style={{ fontSize: '14px', color: C.muted, lineHeight: '1.8', maxWidth: '780px' }}>
            Esta guía reúne las dudas más probables durante la beta: acceso, registro, aceptación legal, uso general, lectura de resultados y la mejor forma de pedir ayuda o dar feedback.
          </div>
        </div>

        <div style={{ display: 'grid', gap: '18px' }}>
          {FAQS.map((group) => (
            <div key={group.category} style={sectionStyle}>
              <div style={{ fontSize: '22px', fontWeight: '500', marginBottom: '16px' }}>{group.category}</div>
              <div style={{ display: 'grid', gap: '14px' }}>
                {group.items.map((item) => (
                  <div key={item.q} style={{ paddingBottom: '14px', borderBottom: `0.5px solid ${C.border}` }}>
                    <div style={{ fontSize: '15px', fontWeight: '500', color: C.text, marginBottom: '6px' }}>{item.q}</div>
                    <div style={{ fontSize: '13px', color: C.muted, lineHeight: '1.8' }}>{item.a}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div style={{ ...sectionStyle, marginTop: '18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '19px', fontWeight: '500', marginBottom: '6px' }}>¿No encontraste tu respuesta?</div>
            <div style={{ fontSize: '13px', color: C.muted, lineHeight: '1.7' }}>
              Escríbenos y te ayudamos. Si es un bug o fricción del producto, mejor aún: eso nos ayuda a mejorar la beta.
            </div>
          </div>
          <button onClick={() => navigate('/feedback')} style={{ padding: '12px 18px', background: C.blue, color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
            Feedback y contacto
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '24px' }}>
          <button onClick={() => navigate(fromBetaAccess ? '/beta-access' : fromRegister ? '/login?mode=register' : (session ? '/dashboard' : '/'))} style={{ fontSize: '12px', color: C.blue, background: 'none', border: 'none', cursor: 'pointer' }}>
            {fromBetaAccess ? 'Volver a acceso beta' : fromRegister ? 'Volver al registro' : session ? 'Volver al dashboard' : 'Volver al inicio'}
          </button>
          <button onClick={() => navigate('/terminos')} style={{ fontSize: '12px', color: C.muted, background: 'none', border: 'none', cursor: 'pointer' }}>
            Terminos y Condiciones
          </button>
        </div>

        <Footer theme={theme} />
      </div>
    </div>
  )
}

